import React, { useMemo, useState } from 'react';
import { MinusIcon, PlusIcon, PlusCircleIcon, TrashIcon } from 'lucide-react';
import { products } from '../../data/products';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { useTranslation } from '../../lib/i18n';
const WEB3FORMS_ACCESS_KEY = 'b7789131-e060-4196-aa41-6a4c61a3a4d1';
const WEB3FORMS_ACTION = 'https://api.web3forms.com/submit';
interface InquiryFormProps {
  preselectedProductId?: string;
  onSuccess: () => void;
}
interface OrderLine {
  id: string;
  productId: string;
  quantity: number;
  batteries: number;
}
function withVat(price: number) {
  return Math.round(price * 1.2);
}
function getBatteryPrice(productId: string) {
  const p = products.find(x => x.id === productId);
  return p?.series === 'scc' ? 375 : 250;
}
function lineTotal(line: OrderLine) {
  const p = products.find(x => x.id === line.productId);
  if (!p) return 0;
  return p.price * line.quantity + getBatteryPrice(line.productId) * line.batteries;
}
let lineIdCounter = 1;
export function InquiryForm({
  preselectedProductId,
  onSuccess
}: InquiryFormProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const [customerType, setCustomerType] = useState<'business' | 'individual'>('business');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([{
    id: String(lineIdCounter++),
    productId: preselectedProductId || '',
    quantity: 1,
    batteries: 0
  }]);
  const updateLine = (id: string, field: keyof OrderLine, value: string | number) => {
    setOrderLines(lines => lines.map(l => l.id === id ? {
      ...l,
      [field]: value
    } : l));
  };
  const addLine = () => {
    setOrderLines(lines => [...lines, {
      id: String(lineIdCounter++),
      productId: '',
      quantity: 1,
      batteries: 0
    }]);
  };
  const removeLine = (id: string) => {
    setOrderLines(lines => lines.filter(l => l.id !== id));
  };
  const grandTotal = orderLines.reduce((sum, l) => sum + lineTotal(l), 0);
  const hasAnyProduct = orderLines.some(l => l.productId !== '');
  const orderSummary = useMemo(() => {
    let summary = '';
    const productNames: string[] = [];
    let total = 0;
    orderLines.forEach((line, idx) => {
      const p = products.find(x => x.id === line.productId);
      if (!p) return;
      const bPrice = getBatteryPrice(line.productId);
      const lt = lineTotal(line);
      total += lt;
      productNames.push(`${p.name} (${p.model}) x${line.quantity}`);
      summary += `\n${idx + 1}. ${p.name} (${p.model})\n`;
      summary += `   Cena: EUR ${p.price.toLocaleString()} bez PDV x ${line.quantity} kom = EUR ${(p.price * line.quantity).toLocaleString()} bez PDV\n`;
      if (line.batteries > 0) {
        summary += `   Rezervne baterije: ${line.batteries} x EUR ${bPrice} = EUR ${(bPrice * line.batteries).toLocaleString()} bez PDV\n`;
      }
      summary += `   Ukupno: EUR ${lt.toLocaleString()} bez PDV (EUR ${withVat(lt).toLocaleString()} sa PDV)\n`;
    });
    return {
      text: summary || '(nije izabran nijedan proizvod)',
      names: productNames.join(', ') || '(nije izabran)',
      total
    };
  }, [orderLines]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    if (!hasAnyProduct) {
      setSubmitError(en ? 'Please select at least one machine.' : ru ? 'Пожалуйста, выберите хотя бы одну машину.' : 'Molimo izaberite barem jednu mašinu.');
      return;
    }
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `Novi upit (${customerType === 'business' ? 'Pravno lice' : 'Fizicko lice'}) - D-TECH Solutions`,
      from_name: 'D-TECH sajt - upit',
      botcheck: '',
      Tip_kupca: customerType === 'business' ? 'Pravno lice' : 'Fizicko lice',
      Modeli: orderSummary.names,
      Detalji_narudzbine: orderSummary.text,
      Ukupna_cena_bez_PDV: `EUR ${orderSummary.total.toLocaleString()}`,
      Ukupna_cena_sa_PDV: `EUR ${withVat(orderSummary.total).toLocaleString()}`
    };
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        payload[key] = value;
      }
    });
    setIsSubmitting(true);
    try {
      const response = await fetch(WEB3FORMS_ACTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        // GA4 lead conversion tracking
        if (typeof (window as any).gtag === 'function') {
          ;
          (window as any).gtag('event', 'generate_lead', {
            currency: 'EUR',
            value: orderSummary.total,
            customer_type: customerType,
            products: orderSummary.names
          });
        }
      } else {
        setIsSubmitting(false);
        setSubmitError(en ? 'Sending failed. Please try again or contact us directly.' : ru ? 'Отправка не удалась. Пожалуйста, попробуйте еще раз или свяжитесь с нами напрямую.' : 'Slanje nije uspelo. Pokušajte ponovo ili nas kontaktirajte direktno.');
      }
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError(en ? 'Network error. Please try again or contact us directly.' : ru ? 'Ошибка сети. Пожалуйста, попробуйте еще раз или свяжитесь с нами напрямую.' : 'Greška u mreži. Pokušajte ponovo ili nas kontaktirajte direktno.');
    }
  };
  if (submitSuccess) {
    return <div className="text-center py-8" data-id="element-58">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-59">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-60">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-61" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2" data-id="element-62">
          {en ? 'Successfully sent!' : ru ? 'Успешно отправлено!' : 'Uspešno poslato!'}
        </h3>
        <p className="text-gray-300 mb-4" data-id="element-63">
          {en ? 'Our team will contact you as soon as possible.' : ru ? 'Наша команда свяжется с вами в ближайшее время.' : 'Naš tim će vas kontaktirati u najkraćem roku.'}
        </p>
        <p className="text-sm text-brand-yellow font-semibold" data-id="element-64">
          {en ? 'We respond to inquiries within 24h.' : ru ? 'Мы отвечаем на запросы в течение 24 часов.' : 'Odgovaramo na upit u roku od 24h.'}
        </p>
        <Button variant="primary" size="md" onClick={onSuccess} className="mt-6" data-id="element-65">
          {en ? 'Close' : ru ? 'Закрыть' : 'Zatvori'}
        </Button>
      </div>;
  }
  const productSelectOptions = [{
    value: '',
    label: en ? 'Select model' : ru ? 'Выберите модель' : 'Izaberite model'
  }, ...products.map(p => {
    const disabled = p.soldOut || p.comingSoon;
    const badgeText = p.soldOut ? en ? 'Sold out' : ru ? 'Продано' : 'Prodato' : p.comingSoon ? en ? 'Coming soon' : ru ? 'Скоро' : 'Uskoro dostupno' : '';
    return {
      value: disabled ? '' : p.id,
      label: disabled ? `${p.name} (${p.model}) — ${badgeText}` : `${p.name} (${p.model}) — €${p.price.toLocaleString()} ${en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}`,
      disabled
    };
  })];
  const counterBtn = 'w-8 h-8 bg-dark-surface border border-dark-border rounded flex items-center justify-center text-white hover:border-brand-yellow disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  const orderSection = <div className="space-y-3" data-id="element-66">
      <label className="block text-sm font-semibold text-white" data-id="element-67">
        {en ? 'Machines and equipment *' : ru ? 'Машины и оборудование *' : 'Mašine i oprema *'}
      </label>

      {orderLines.map((line, idx) => {
      const selectedProduct = products.find(p => p.id === line.productId);
      const bPrice = getBatteryPrice(line.productId);
      const lt = lineTotal(line);
      return <div key={line.id} className="bg-dark-bg border border-dark-border rounded-industrial p-4 space-y-3" data-id="element-68">
            <div className="flex items-center justify-between" data-id="element-69">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-70">
                {en ? `Item ${idx + 1}` : ru ? `Позиция ${idx + 1}` : `Stavka ${idx + 1}`}
              </span>
              {orderLines.length > 1 && <button type="button" onClick={() => removeLine(line.id)} className="text-gray-600 hover:text-red-400 transition-colors" aria-label={en ? 'Remove item' : ru ? 'Удалить позицию' : 'Ukloni stavku'} data-id="element-71">
                  <TrashIcon className="w-4 h-4" data-id="element-72" />
                </button>}
            </div>

            <select value={line.productId} onChange={e => updateLine(line.id, 'productId', e.target.value)} className="w-full px-3 py-2.5 bg-dark-surface text-white border border-dark-border rounded-industrial text-sm focus:outline-none focus:border-brand-yellow transition-colors appearance-none" data-id="element-73">
              {productSelectOptions.map(opt => <option key={opt.value + opt.label} value={opt.value} disabled={opt.disabled} data-id="element-74">
                  {opt.label}
                </option>)}
            </select>

            {selectedProduct && <>
                <div className="grid grid-cols-2 gap-3" data-id="element-75">
                  <div data-id="element-76">
                    <p className="text-xs text-gray-500 mb-1.5" data-id="element-77">
                      {en ? 'Quantity' : ru ? 'Количество' : 'Količina mašina'}
                    </p>
                    <div className="flex items-center gap-2" data-id="element-78">
                      <button type="button" onClick={() => updateLine(line.id, 'quantity', Math.max(1, line.quantity - 1))} disabled={line.quantity === 1} className={counterBtn} data-id="element-79">
                        <MinusIcon className="w-3.5 h-3.5" data-id="element-80" />
                      </button>
                      <span className="text-lg font-bold text-white w-8 text-center" data-id="element-81">
                        {line.quantity}
                      </span>
                      <button type="button" onClick={() => updateLine(line.id, 'quantity', line.quantity + 1)} className={counterBtn} data-id="element-82">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-83" />
                      </button>
                    </div>
                  </div>
                  <div data-id="element-84">
                    <p className="text-xs text-gray-500 mb-1.5" data-id="element-85">
                      {en ? 'Spare batteries' : ru ? 'Запасные батареи' : 'Rezervne baterije'}
                      <span className="text-gray-600 ml-1" data-id="element-86">(€{bPrice})</span>
                    </p>
                    <div className="flex items-center gap-2" data-id="element-87">
                      <button type="button" onClick={() => updateLine(line.id, 'batteries', Math.max(0, line.batteries - 1))} disabled={line.batteries === 0} className={counterBtn} data-id="element-88">
                        <MinusIcon className="w-3.5 h-3.5" data-id="element-89" />
                      </button>
                      <span className="text-lg font-bold text-white w-8 text-center" data-id="element-90">
                        {line.batteries}
                      </span>
                      <button type="button" onClick={() => updateLine(line.id, 'batteries', line.batteries + 1)} className={counterBtn} data-id="element-91">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-92" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-baseline justify-between pt-1 border-t border-dark-border" data-id="element-93">
                  <span className="text-xs text-gray-500" data-id="element-94">
                    {en ? 'Item total' : ru ? 'Итого по позиции' : 'Stavka ukupno'}
                  </span>
                  <div className="text-right" data-id="element-95">
                    <span className="text-brand-yellow font-bold" data-id="element-96">
                      €{lt.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 ml-1" data-id="element-97">
                      {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
                    </span>
                    <p className="text-xs text-gray-600" data-id="element-98">
                      {en ? 'incl. VAT' : ru ? 'с НДС' : 'sa PDV'}: €
                      {withVat(lt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </>}
          </div>;
    })}

      <button type="button" onClick={addLine} className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-dark-border rounded-industrial text-sm text-gray-500 hover:text-brand-yellow hover:border-brand-yellow transition-colors" data-id="element-99">
        <PlusCircleIcon className="w-4 h-4" data-id="element-100" />
        {en ? 'Add another machine' : ru ? 'Добавить еще одну машину' : 'Dodaj još jednu mašinu'}
      </button>

      {hasAnyProduct && <div className="p-4 bg-brand-yellow bg-opacity-10 border-2 border-brand-yellow rounded-industrial" data-id="element-101">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-102">
            {en ? 'Total order price' : ru ? 'Общая стоимость заказа' : 'Ukupna cena narudžbine'}
          </p>
          <p className="text-2xl font-black text-brand-yellow" data-id="element-103">
            €{grandTotal.toLocaleString()}
            <span className="text-sm font-normal text-gray-400 ml-2" data-id="element-104">
              {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
            </span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-105">
            {en ? 'incl. VAT' : ru ? 'с НДС' : 'Sa PDV'}: €
            {withVat(grandTotal).toLocaleString()}
          </p>
        </div>}
    </div>;
  const businessForm = <form onSubmit={handleSubmit} className="space-y-6" noValidate data-id="element-106">
      {orderSection}
      <div className="border-t-2 border-dark-border pt-6" data-id="element-107">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-108">
          {en ? 'Company details' : ru ? 'Реквизиты компании' : 'Podaci o kompaniji'}
        </h3>
        <div className="space-y-4" data-id="element-109">
          <Input label={en ? 'Company name *' : ru ? 'Название компании *' : 'Naziv kompanije *'} type="text" name="Naziv_kompanije" required placeholder={en ? 'Your company name' : ru ? 'Название вашей компании' : 'Naziv vaše kompanije'} data-id="element-110" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-111">
            <Input label={en ? 'VAT number *' : ru ? 'ИНН *' : 'PIB *'} type="text" name="PIB" required placeholder="123456789" data-id="element-112" />
            <Input label={en ? 'Registration number *' : ru ? 'ОГРН *' : 'Matični broj *'} type="text" name="Maticni_broj" required placeholder="12345678" data-id="element-113" />
          </div>
          <Input label={en ? 'Address *' : ru ? 'Адрес *' : 'Adresa *'} type="text" name="Adresa" required placeholder={en ? 'Street and number' : ru ? 'Улица и номер' : 'Ulica i broj'} data-id="element-114" />
          <Input label={en ? 'City *' : ru ? 'Город *' : 'Grad *'} type="text" name="Grad" required placeholder={en ? 'City' : ru ? 'Город' : 'Grad'} data-id="element-115" />
          <Input label={en ? 'Contact person *' : ru ? 'Контактное лицо *' : 'Kontakt osoba *'} type="text" name="Kontakt_osoba" required placeholder={en ? 'Full name' : ru ? 'Полное имя' : 'Ime i prezime'} data-id="element-116" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-117">
            <Input label={en ? 'Email *' : 'Email *'} type="email" name="email" required placeholder={en ? 'contact@company.com' : ru ? 'kontakt@kompanija.com' : 'kontakt@kompanija.com'} data-id="element-118" />
            <Input label={en ? 'Phone *' : ru ? 'Телефон *' : 'Telefon *'} type="tel" name="Telefon" required placeholder={en ? '+1 ...' : ru ? '+7 ...' : '+381 11 123 4567'} data-id="element-119" />
          </div>
          <Textarea label={en ? 'Message (optional)' : ru ? 'Сообщение (необязательно)' : 'Poruka (opciono)'} name="Poruka" placeholder={en ? 'Additional information or questions' : ru ? 'Дополнительная информация или вопросы' : 'Dodatne informacije ili pitanja'} rows={3} data-id="element-120" />
        </div>
      </div>
      {submitError && <div className="p-4 bg-red-500/10 border-2 border-red-500/50 rounded-industrial text-sm text-red-300" data-id="element-121">
          {submitError}
        </div>}
      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full" data-id="element-122">
        {isSubmitting ? en ? 'Sending...' : ru ? 'Отправка...' : 'Šaljem...' : en ? 'Send inquiry' : ru ? 'Отправить запрос' : 'Pošalji upit'}
      </Button>
    </form>;
  const individualForm = <form onSubmit={handleSubmit} className="space-y-6" noValidate data-id="element-123">
      {orderSection}
      <div className="border-t-2 border-dark-border pt-6" data-id="element-124">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-125">
          {en ? 'Your details' : ru ? 'Ваши данные' : 'Vaši podaci'}
        </h3>
        <div className="space-y-4" data-id="element-126">
          <Input label={en ? 'Full name *' : ru ? 'Полное имя *' : 'Ime i prezime *'} type="text" name="Ime_i_prezime" required placeholder={en ? 'Your full name' : ru ? 'Ваше полное имя' : 'Vaše ime i prezime'} data-id="element-127" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-128">
            <Input label={en ? 'Email *' : 'Email *'} type="email" name="email" required placeholder={en ? 'your@email.com' : ru ? 'vas.email@primer.com' : 'vas.email@primer.com'} data-id="element-129" />
            <Input label={en ? 'Phone *' : ru ? 'Телефон *' : 'Telefon *'} type="tel" name="Telefon" required placeholder={en ? '+1 ...' : ru ? '+7 ...' : '+381 60 123 4567'} data-id="element-130" />
          </div>
          <Input label={en ? 'City *' : ru ? 'Город *' : 'Grad *'} type="text" name="Grad" required placeholder={en ? 'Your city' : ru ? 'Ваш город' : 'Vaš grad'} data-id="element-131" />
          <Textarea label={en ? 'Message (optional)' : ru ? 'Сообщение (необязательно)' : 'Poruka (opciono)'} name="Poruka" placeholder={en ? 'Additional information or questions' : ru ? 'Дополнительная информация или вопросы' : 'Dodatne informacije ili pitanja'} rows={3} data-id="element-132" />
        </div>
      </div>
      {submitError && <div className="p-4 bg-red-500/10 border-2 border-red-500/50 rounded-industrial text-sm text-red-300" data-id="element-133">
          {submitError}
        </div>}
      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full" data-id="element-134">
        {isSubmitting ? en ? 'Sending...' : ru ? 'Отправка...' : 'Šaljem...' : en ? 'Send inquiry' : ru ? 'Отправить запрос' : 'Pošalji upit'}
      </Button>
    </form>;
  return <Tabs tabs={[{
    id: 'business',
    label: en ? 'Company' : ru ? 'Юридическое лицо' : 'Pravno lice',
    content: businessForm
  }, {
    id: 'individual',
    label: en ? 'Individual' : ru ? 'Физическое лицо' : 'Fizičko lice',
    content: individualForm
  }]} defaultTab="business" onChange={id => setCustomerType(id as 'business' | 'individual')} data-id="element-135" />;
}